import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import {
  type AuditRow,
  type Connector,
  type Grant,
  type StudioMe,
  getStudioMe as apiMe,
  grantConnector as apiGrant,
  listAudit as apiAudit,
  listConnectors as apiConnectors,
  revokeConnector as apiRevoke,
  studioErrorMessage,
} from '../../components/AgentStudio/api';

export type StudioTab = 'appearance' | 'workspace' | 'evidence' | 'extension' | 'mcp' | 'connectors' | 'company-brain' | 'agents' | 'refresh' | 'access' | 'models' | 'support' | 'parked'
  // the simplified app's own sections (#1513); today's page lands them on Appearance
  | 'explanations' | 'watches';

/** The Settings tab on screen: agents have their own page, and support is platform owners' only (#472 item 3). */
export const shownStudioTab = (tab: StudioTab, platformOwner: boolean): StudioTab =>
  tab === 'agents' || (tab === 'support' && !platformOwner) ? 'connectors' : tab;

export interface AgentStudioSlice {
  connectors: Connector[];
  audit: AuditRow[];
  studioMe: StudioMe | null;
  studioTab: StudioTab;
  studioLoading: boolean;
  studioError: string | null;
  /** Bumped on every identity/workspace switch; a fetch that outlived one is dropped. */
  studioGeneration: number;

  setStudioTab: (tab: StudioTab) => void;
  /** The workspace changed: forget every connector, grant and audit row — they belong to the one we left. */
  resetStudioWorkspace: () => void;
  fetchStudio: () => Promise<void>;
  /** Grant `read` on a connector; the row carries the signed grant, and the audit refreshes. */
  grantConnector: (connectionId: string) => Promise<Grant>;
  /** Revoke the live grant; the row shows no grant, and the audit refreshes. */
  revokeConnector: (connectionId: string) => Promise<Grant>;
}

export const createAgentStudioSlice: StateCreator<
  VoyagerState, [], [], AgentStudioSlice
> = (set, get) => {
  /**
   * Every request captures the workspace generation before it goes out and
   * applies its result only if no identity/org switch happened meanwhile.
   * The result is still returned, so a caller's own flow is unaffected.
   */
  const guarded = async <T,>(work: () => Promise<T>, apply: (result: T) => void): Promise<T> => {
    const gen = get().studioGeneration;
    const result = await work();
    if (get().studioGeneration === gen) apply(result);
    return result;
  };
  const withGrant = (connectionId: string, grant: Grant) => set((s) => ({
    connectors: s.connectors.map((c) => (c.connection.id === connectionId ? { ...c, grant } : c)),
  }));
  const refreshAudit = () => guarded(() => apiAudit(), (audit) => set({ audit })).catch(() => {});

  return {
  connectors: [],
  audit: [],
  studioMe: null,
  studioTab: 'connectors',
  studioLoading: false,
  studioError: null,
  studioGeneration: 0,

  setStudioTab: (tab) => set({ studioTab: tab }),

  resetStudioWorkspace: () => {
    const onStudio = get().currentView === 'agent_studio';
    set({
      connectors: [], audit: [], studioMe: null, studioLoading: false, studioError: null,
      studioGeneration: get().studioGeneration + 1,
    });
    if (onStudio) get().fetchStudio().catch(() => {});
  },

  fetchStudio: async () => {
    const gen = get().studioGeneration;
    set({ studioLoading: true, studioError: null });
    try {
      const [connectors, audit, studioMe] = await Promise.all([apiConnectors(), apiAudit(), apiMe()]);
      if (get().studioGeneration !== gen) return;
      set({ connectors, audit, studioMe, studioLoading: false });
    } catch (e: any) {
      if (get().studioGeneration !== gen) return;
      set({ studioLoading: false, studioError: studioErrorMessage(e, 'Could not load the studio') });
    }
  },

  grantConnector: async (connectionId) => {
    const grant = await guarded(() => apiGrant(connectionId), (g) => withGrant(connectionId, g));
    refreshAudit();
    return grant;
  },

  revokeConnector: async (connectionId) => {
    const grant = await guarded(() => apiRevoke(connectionId), (g) => withGrant(connectionId, g));
    refreshAudit();
    return grant;
  },
  };
};
